<template>
  <div>
    <nav class="flex gap-6 border-b border-gray-200 mb-6" aria-label="Test sections">
      <router-link
        v-for="t in subTabs"
        :key="t.key"
        :to="{ name: t.route }"
        :class="['py-2 font-medium border-b-2', t.key === sub ? 'border-primary-500 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-900']"
      >
        {{ t.label }}
      </router-link>
    </nav>

    <LoadingIndicator v-if="loading" />
    <p v-else-if="error" class="text-red-600">{{ error }}</p>

    <HermesCard v-else-if="!bench.template || !bench.executor" title="Set up the agent bench" meta="One time, for this tenant">
      <p class="text-gray-700 mb-4">
        The bench needs two rows Hermes will keep: a judge that grades each answer, and the list of test cases.
        Setting up creates both, with {{ seedCount }} seed cases you can edit afterwards.
      </p>
      <label class="block mb-4 max-w-md">
        <span class="block text-gray-500 mb-2">Judge model</span>
        <input v-model="judgeModel" :class="INPUT" list="bench-judge-models">
        <datalist id="bench-judge-models"><option v-for="m in judgeModels" :key="m" :value="m" /></datalist>
      </label>
      <div class="flex items-center gap-3">
        <button type="button" :class="PRIMARY_BUTTON" :disabled="busy || !judgeModel.trim()" @click="setUp">{{ busy ? 'Setting up…' : 'Set up the bench' }}</button>
        <span v-if="setupError" class="text-red-600">{{ setupError }}</span>
      </div>
    </HermesCard>

    <component :is="view" v-else :bench="bench" @refresh="load" @crumb="$emit('crumb', $event)" />
  </div>
</template>

<script>
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import HermesCard from '../HermesCard.vue';
import { INPUT, PRIMARY_BUTTON, errorOf } from '../hermes';
import { SUB_TABS, findBench, setUpBench, seedCases } from './bench';
import TestCases from './TestCases.vue';
import TestRuns from './TestRuns.vue';

const VIEWS = { AdminHermesTests: 'TestCases', AdminHermesTestRuns: 'TestRuns' };
const SUB_OF = { AdminHermesTests: 'cases', AdminHermesTestRuns: 'runs' };

export default {
  name: 'HermesTests',
  components: { LoadingIndicator, HermesCard, TestCases, TestRuns },
  emits: ['crumb', 'pending'],
  data() {
    return {
      bench: { executor: null, template: null }, loading: true, error: '', busy: false, setupError: '',
      judgeModel: 'claude-opus-4-6', judgeModels: ['claude-opus-4-6', 'claude-sonnet-5', 'gpt-5.6-luna', 'gemini-3.8-flash'],
      subTabs: SUB_TABS, INPUT, PRIMARY_BUTTON,
    };
  },
  computed: {
    view() {
      return VIEWS[this.$route.name] || 'TestCases';
    },
    sub() {
      return SUB_OF[this.$route.name] || 'cases';
    },
    seedCount() {
      return seedCases().length;
    },
  },
  async mounted() {
    await this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = '';
      try {
        this.bench = await findBench(this.$axios);
      } catch (e) {
        this.error = errorOf(e, 'The bench could not be loaded.');
      } finally {
        this.loading = false;
      }
    },
    async setUp() {
      this.busy = true;
      this.setupError = '';
      try {
        this.bench = await setUpBench(this.$axios, this.judgeModel.trim());
      } catch (e) {
        this.setupError = errorOf(e, 'The bench could not be set up.');
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>
